/**
 * `aiRealtimeContentTypes.ts` — the three content-definition value types used by the
 * progressive-disclosure catalogs (port of the definition classes in Unity's
 * `AIRealtimeRoleCatalog.cs`, `AIRealtimePersonalityCatalog.cs`, `AIRealtimeSkillCatalog.cs`).
 *
 * Kept in a standalone module (not inside the catalog files) so the module graph stays acyclic:
 * the personality catalog imports `agentPrompts` (to register definitions) while `agentPrompts`
 * needs these types — neither may import the other's module.
 */

import { normalizeTokens } from "./aiRealtimeMarkdownFrontmatter";

/**
 * A role persona loaded from `/AI/roles/*.md`. The markdown body is injected into the system
 * prompt as the current-role framing (see `setRoleDefinitions` in `agentPrompts.ts`).
 */
export class AIRealtimeRoleDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly fileName: string;
  readonly markdown: string;
  readonly description: string | null;
  /**
   * Tool ids this role may use. Empty means the role imposes no tool restriction (all enabled
   * tools remain available).
   */
  readonly tools: readonly string[];
  /**
   * The file's bytes exactly as loaded — frontmatter fence, YAML fields, body,
   * original whitespace, **un-trimmed**, unlike `markdown` above.
   *
   * This is what the debug panel's Download button emits, and it has to be the
   * raw text rather than `markdown`: `markdown` is the frontmatter-STRIPPED body,
   * so downloading it would drop `tools:` (which is frontmatter-authoritative —
   * no manifest entry sets it) and the file would not round-trip through an
   * upload. Empty for a definition built without a source file.
   */
  readonly rawText: string;
  /** Loaded from a developer override rather than from `public/AI/roles/`. */
  readonly isPublished: boolean;

  constructor(
    id: string,
    displayName: string,
    fileName: string,
    markdown: string,
    description: string | null = null,
    tools: readonly string[] | null = null,
    rawText: string | null = null,
    isPublished = false,
  ) {
    this.id = id?.trim() ?? "";
    this.displayName = displayName?.trim() ?? "";
    this.fileName = fileName?.trim() ?? "";
    this.markdown = markdown?.trim() ?? "";
    this.description = description?.trim() || null;
    this.tools = normalizeTokens(tools);
    this.rawText = rawText ?? "";
    this.isPublished = isPublished;
  }
}

/**
 * A personality loaded from `/AI/personalities/*.md`. The markdown body is the voice rules
 * injected into the system prompt's `{personality_voice}` slot; frontmatter carries the realtime
 * voice and Spark appearance.
 */
export class AIRealtimePersonalityDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly fileName: string;
  /** Voice rules markdown body, used as the personality's additional voice instructions. */
  readonly markdown: string;
  /** Spark appearance name to apply for this personality (optional). */
  readonly appearanceName: string | null;
  /** Realtime voice name to use for this personality (optional). */
  readonly voiceName: string | null;
  readonly description: string | null;

  constructor(
    id: string,
    displayName: string,
    fileName: string,
    markdown: string,
    appearanceName: string | null = null,
    voiceName: string | null = null,
    description: string | null = null,
  ) {
    this.id = id?.trim() ?? "";
    this.displayName = displayName?.trim() ?? "";
    this.fileName = fileName?.trim() ?? "";
    this.markdown = markdown?.trim() ?? "";
    this.appearanceName = appearanceName?.trim() || null;
    this.voiceName = voiceName?.trim() || null;
    this.description = description?.trim() || null;
  }
}

/**
 * A skill loaded from `/AI/skills/*.md`. Skills are indexed (id + description) in the prompt and
 * only expanded to their full markdown body after the model calls `Load_Skill`.
 */
export class AIRealtimeSkillDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly fileName: string;
  readonly markdown: string;
  readonly description: string | null;
  /** Roles this skill is available in (normalized, lower-case). Empty means every role. */
  readonly roles: readonly string[];
  /**
   * Tool ids this skill unlocks when loaded (added on top of the active role's tools). Empty
   * means the skill declares no tools of its own.
   */
  readonly tools: readonly string[];
  /** The file's bytes exactly as loaded, un-trimmed — see {@link AIRealtimeRoleDefinition.rawText}. */
  readonly rawText: string;
  /** Loaded from a developer override rather than from `public/AI/skills/`. */
  readonly isPublished: boolean;

  constructor(
    id: string,
    displayName: string,
    fileName: string,
    markdown: string,
    description: string | null = null,
    roles: readonly string[] | null = null,
    tools: readonly string[] | null = null,
    rawText: string | null = null,
    isPublished = false,
  ) {
    this.id = id?.trim() ?? "";
    this.displayName = displayName?.trim() ?? "";
    this.fileName = fileName?.trim() ?? "";
    this.markdown = markdown?.trim() ?? "";
    this.description = description?.trim() || null;
    this.roles = AIRealtimeSkillDefinition.normalizeRoles(roles);
    this.tools = normalizeTokens(tools);
    this.rawText = rawText ?? "";
    this.isPublished = isPublished;
  }

  appliesToRole(roleId: string | null | undefined): boolean {
    if (this.roles.length === 0 || !roleId || !roleId.trim()) {
      return true;
    }

    const target = roleId.trim().toLowerCase();
    return this.roles.some((role) => role === target);
  }

  private static normalizeRoles(roles: readonly string[] | null | undefined): string[] {
    if (!roles || roles.length === 0) {
      return [];
    }

    const normalized: string[] = [];
    for (const role of roles) {
      if (!role || !role.trim()) {
        continue;
      }

      const trimmed = role.trim().toLowerCase();
      if (!normalized.includes(trimmed)) {
        normalized.push(trimmed);
      }
    }

    return normalized;
  }
}
